import { useRef, type ReactNode, useId } from "react";
import { createPortal } from "react-dom";
import { Icon } from "../brand";
import { useEscape, useFocusTrap, useBodyScrollLock } from "./overlay";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Max width of the dialog. */
  width?: number;
}

/** Centered modal dialog. Backdrop blur, focus trap, ESC, ARIA. */
export function Modal({ open, onClose, title, children, footer, width = 480 }: ModalProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEscape(open, onClose);
  useFocusTrap(ref, open);
  useBodyScrollLock(open);

  if (!open) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "grid",
        placeItems: "center",
        padding: 20,
        background: "rgba(10,10,11,0.6)",
        backdropFilter: "blur(4px)",
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        style={{
          width: "100%",
          maxWidth: width,
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          boxShadow: "0 24px 64px -24px rgba(0,0,0,.6)",
          outline: "none",
          animation: "vocFadeUp 250ms cubic-bezier(0.16,1,0.3,1)",
        }}
      >
        {title ? (
          <header
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 20px",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <h2 id={titleId} style={{ fontSize: 16, fontWeight: 600 }}>
              {title}
            </h2>
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              style={{
                display: "grid",
                placeItems: "center",
                width: 28,
                height: 28,
                borderRadius: 6,
                border: "1px solid transparent",
                background: "transparent",
                color: "var(--fg-muted)",
                cursor: "pointer",
              }}
            >
              <Icon name="x" size={18} />
            </button>
          </header>
        ) : null}
        <div style={{ padding: 20 }}>{children}</div>
        {footer ? (
          <footer
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              padding: "14px 20px",
              borderTop: "1px solid var(--border)",
            }}
          >
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
